/**
 * CUSTOMER-R4.15-K9 / R7.2.2F2 — one canonical 3-product recommendation block
 * for cart / pre-checkout / final checkout.
 */
import { CrossSellBlock } from "./CrossSellBlock.js";
import type { CrossSellCandidate } from "./cross-sell-rules.js";
import type { CatalogV2Product } from "./catalog-v2-types.js";
import type { StoreV2Lang } from "./store-v2-locale.js";

export interface CanonicalThreeProductRecommendationsProps {
  lang: StoreV2Lang;
  items: CrossSellCandidate[];
  onAdd: (product: CatalogV2Product) => void;
  onOpen?: (product: CatalogV2Product) => void;
  onRegisterActionable?: (products: CatalogV2Product[]) => void;
  title?: string;
  testId?: string;
  onRefresh: () => void;
  canRefresh?: boolean;
  refreshing?: boolean;
  loading?: boolean;
  onImpression?: (ids: string[]) => void;
  placement: "CART_CROSS_SELL" | "PRECHECKOUT_CROSS_SELL" | "CHECKOUT_CROSS_SELL";
}

export function CanonicalThreeProductRecommendations({
  lang,
  items,
  onAdd,
  onOpen,
  onRegisterActionable,
  title,
  testId,
  onRefresh,
  canRefresh = false,
  refreshing = false,
  loading = false,
  onImpression,
  placement,
}: CanonicalThreeProductRecommendationsProps) {
  const list = items.slice(0, 3);
  if (list.length === 0 && loading) {
    return (
      <div
        className="sv2-xsell is-compact"
        data-testid={testId ?? "sv2-crosssell-skeleton"}
        data-canonical-recs="1"
        data-placement={placement}
        aria-busy="true"
      >
        <div className="sv2-xsell-head">
          <div className="sv2-section-title">{title ?? ""}</div>
        </div>
        <div className="sv2-xsell-list">
          {[0, 1, 2].map((i) => (
            <div key={i} className="sv2-xsell-card sv2-xsell-card-skeleton" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <CrossSellBlock
      lang={lang}
      items={list}
      onAdd={onAdd}
      {...(onOpen ? { onOpen } : {})}
      {...(onRegisterActionable ? { onRegisterActionable } : {})}
      variant="cart"
      {...(title ? { title } : {})}
      {...(testId ? { testId } : {})}
      onRefresh={onRefresh}
      canRefresh={canRefresh}
      refreshing={refreshing}
      {...(onImpression ? { onImpression } : {})}
      canonical
      placement={placement}
    />
  );
}
